<template>
  <v-dialog
    v-model="localShow"
    max-width="800px"
    persistent
    scrollable
    @click:outside="handleCancel"
    @keydown.esc="handleCancel"
  >
    <v-card class="incident-form-dialog">
      <!-- Header del diálogo -->
      <v-card-title class="d-flex align-center pa-4">
        <v-icon
          :icon="isEditing ? 'mdi-pencil' : 'mdi-plus'"
          size="24"
          class="mr-3"
        ></v-icon>
        <div class="flex-grow-1">
          <h2 class="text-h6 mb-1">
            {{ isEditing ? 'Editar Incidencia' : 'Nueva Incidencia' }}
          </h2>
          <p class="text-caption text-grey mb-0">
            {{ isEditing ? 'Modifica los datos de la incidencia' : 'Completa la información para crear una nueva incidencia' }}
          </p>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="handleCancel"
        ></v-btn>
      </v-card-title>

      <!-- Contenido del formulario -->
      <v-card-text class="pa-6">
        <v-form ref="formRef" v-model="formValid" @submit.prevent="handleSave">
          <v-row>
            <!-- Título de la incidencia -->
            <v-col cols="12">
              <v-text-field
                v-model="formData.title"
                label="Título de la incidencia *"
                variant="outlined"
                :rules="titleRules"
                :error-messages="validationErrors.titleError"
                prepend-inner-icon="mdi-text"
                placeholder="Describe brevemente la incidencia..."
                counter="100"
                maxlength="100"
              ></v-text-field>
            </v-col>

            <!-- Descripción -->
            <v-col cols="12">
              <v-textarea
                v-model="formData.description"
                label="Descripción detallada *"
                variant="outlined"
                :rules="descriptionRules"
                :error-messages="validationErrors.descriptionError"
                prepend-inner-icon="mdi-text-long"
                placeholder="Proporciona todos los detalles relevantes de la incidencia..."
                rows="4"
                counter="500"
                maxlength="500"
              ></v-textarea>
            </v-col>

            <!-- Prioridad y Categoría -->
            <v-col cols="12" md="6">
              <v-select
                v-model="formData.priority"
                :items="priorityOptions"
                label="Prioridad *"
                variant="outlined"
                :rules="priorityRules"
                :error-messages="validationErrors.priorityError"
                prepend-inner-icon="mdi-flag"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template v-slot:prepend>
                      <v-icon
                        :icon="getPriorityIcon(item.value)"
                        :color="getPriorityColor(item.value)"
                        size="20"
                      ></v-icon>
                    </template>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-icon
                      :icon="getPriorityIcon(item.value)"
                      :color="getPriorityColor(item.value)"
                      size="20"
                      class="mr-2"
                    ></v-icon>
                    {{ item.title }}
                  </div>
                </template>
              </v-select>
            </v-col>

            <!-- P13-V1-V6B: categoría y agencia sólo existen en la CREACIÓN. El backend no las
                 devuelve (no son columnas de `Incidence`, que modela grupo/punto/clasificación) ni
                 las acepta en la actualización genérica, así que en edición no hay valor que
                 mostrar ni cambio que enviar: pintarlas vacías y obligatorias dejaba el formulario
                 imposible de validar y la edición muerta. -->
            <v-col v-if="!isEditing" cols="12" md="6">
              <v-select
                v-model="formData.category"
                :items="categoryOptions"
                label="Categoría *"
                variant="outlined"
                :rules="categoryRules"
                :error-messages="validationErrors.categoryError"
                prepend-inner-icon="mdi-tag"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template v-slot:prepend>
                      <v-icon
                        :icon="getCategoryIcon(item.value)"
                        :color="getCategoryColor(item.value)"
                        size="20"
                      ></v-icon>
                    </template>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <v-icon
                      :icon="getCategoryIcon(item.value)"
                      :color="getCategoryColor(item.value)"
                      size="20"
                      class="mr-2"
                    ></v-icon>
                    {{ item.title }}
                  </div>
                </template>
              </v-select>
            </v-col>

            <!-- P13-V1C: CLASIFICACIÓN obligatoria (sólo creación). El único canal canónico del
                 routing: el servidor deriva la política efectiva y el departamento destino. R2 (F5):
                 el catálogo se CONTEXTUALIZA con la agencia seleccionada (pointId) — sin agencia no
                 hay catálogo, y cambiar de agencia limpia la selección y recarga (advisory — el
                 intake falla cerrado si la política desaparece antes del commit). -->
            <v-col v-if="!isEditing" cols="12" md="6">
              <v-select
                v-model="formData.classificationId"
                :items="classificationOptions"
                :loading="loadingClassifications"
                :disabled="!formData.agencyId"
                item-title="name"
                item-value="id"
                label="Clasificación *"
                variant="outlined"
                :rules="classificationRules"
                :error-messages="validationErrors.classificationError"
                :no-data-text="classificationNoDataText"
                prepend-inner-icon="mdi-shape"
              />
            </v-col>

            <!-- Agencia (sólo creación — ver la nota de categoría) -->
            <v-col v-if="!isEditing" cols="12" md="6">
              <v-autocomplete
                v-model="formData.agencyId"
                :items="agencyOptions"
                label="Agencia afectada *"
                variant="outlined"
                :rules="agencyRules"
                :error-messages="validationErrors.agencyError"
                prepend-inner-icon="mdi-store"
                :loading="loadingAgencies"
                item-title="name"
                item-value="id"
                clearable
                :search="agencySearch"
                @update:search="handleAgencySearch"
              >
                <template v-slot:selection="{ item }">
                  <span>{{ item.title }}</span>
                </template>
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.raw.address }}</v-list-item-subtitle>
                    <template v-slot:append>
                      <v-chip
                        :color="item.raw.status === 'ACTIVE' ? 'success' : 'error'"
                        size="x-small"
                        variant="tonal"
                      >
                        {{ item.raw.status === 'ACTIVE' ? 'Activa' : 'Inactiva' }}
                      </v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- P13-V1-V6B: el estado NO se edita aquí. El ciclo de vida cambia sólo por sus
                 transiciones nombradas (iniciar trabajo, enviar a validación, aceptar/rechazar,
                 reabrir), que registran historial y emiten su evento. Un selector aquí ofrecía una
                 segunda autoridad para lo mismo, y era la que el usuario veía primero. -->

            <!-- Archivos adjuntos — sólo CREACIÓN. La actualización genérica lleva exactamente
                 `title`, `description` y `priority`: no procesa adjuntos ni los persiste. Ofrecer el
                 campo durante la edición prometía una subida que nunca ocurría y devolvía un estado
                 «guardado» por un archivo que el servidor jamás recibió. Adjuntar a una incidencia ya
                 creada es un flujo propio y no existe todavía. -->
            <v-col v-if="!isEditing" cols="12">
              <v-file-input
                v-model="formData.attachments"
                label="Archivos adjuntos"
                variant="outlined"
                multiple
                prepend-inner-icon="mdi-paperclip"
                accept="image/*,application/pdf,.doc,.docx,.txt"
                :show-size="true"
                counter
                chips
              >
                <template v-slot:selection="{ fileNames }">
                  <template v-for="fileName in fileNames" :key="fileName">
                    <v-chip
                      size="small"
                      label
                      color="primary"
                      class="mr-2"
                    >
                      {{ fileName }}
                    </v-chip>
                  </template>
                </template>
              </v-file-input>
              <div class="text-caption text-grey mt-1">
                Archivos permitidos: Imágenes, PDF, Word, Texto. Máximo 10MB por archivo.
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Acciones del diálogo -->
      <v-card-actions class="dialog-actions pa-6 pt-0">
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          @click="handleCancel"
          :disabled="saving"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          @click="handleSave"
          :loading="saving"
          :disabled="!formValid"
        >
          {{ isEditing ? 'Actualizar' : 'Crear' }} Incidencia
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import type {
  Incident,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  Agency,
  IncidentFormData,
  IncidentFormValidationErrors,
  IncidentClassificationOption,
} from '@/types/incidents'
// P13-V1C — catálogo de clasificaciones (advisory; el intake re-resuelve y falla cerrado)
import { incidentsService } from '@/services/incidentsService'

interface Props {
  modelValue: boolean
  incident?: Incident | null
  agencies: Agency[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  save: [data: CreateIncidentRequest | UpdateIncidentRequest]
  cancel: []
}>()

// Función para mostrar notificaciones (simplificada)
const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
  console.log(`${type.toUpperCase()}: ${message}`)
  // En un proyecto real, aquí se integraría con un sistema de notificaciones
}

// Referencias
const formRef = ref()
const formValid = ref(false)
const saving = ref(false)
const loadingAgencies = ref(false)
const agencySearch = ref('')

// Estado del formulario
const formData = ref<IncidentFormData>({
  title: '',
  description: '',
  priority: 'MEDIUM',
  category: 'OTHER',
  agencyId: null,
  classificationId: null,
  status: 'PENDING',
  attachments: []
})

// P13-V1C — catálogo advisory de clasificaciones. R2 (F5): CONTEXTUALIZADO POR PUNTO — se carga
// únicamente con la agencia seleccionada como `pointId`; jamás se pide el catálogo global desde
// esta superficie (una clasificación de otra administración nunca debe ofrecerse ni sobrevivir a
// un cambio de agencia).
const classificationOptions = ref<IncidentClassificationOption[]>([])
const loadingClassifications = ref(false)
const classificationsError = ref<string | null>(null)
// Token de vigencia: una respuesta que llega después de otro cambio de agencia se descarta
// (la carrera carga-tardía-pisa-catálogo-nuevo).
let classificationRequestToken = 0
const classificationRules = [(v: number | null) => !!v || 'La clasificación es obligatoria']

const classificationNoDataText = computed(() => {
  if (loadingClassifications.value) return 'Cargando clasificaciones…'
  if (classificationsError.value) return classificationsError.value
  if (!formData.value.agencyId) return 'Seleccione primero una agencia'
  return 'Sin clasificaciones con política vigente'
})

const loadClassifications = async (pointId: number | null) => {
  if (!pointId) {
    classificationOptions.value = []
    classificationsError.value = null
    return
  }
  const token = ++classificationRequestToken
  loadingClassifications.value = true
  classificationsError.value = null
  try {
    const options = await incidentsService.getClassifications(pointId)
    if (token !== classificationRequestToken) return
    classificationOptions.value = options
    // R2 (F5): una selección previa que no existe en el catálogo del nuevo punto no sobrevive.
    if (
      formData.value.classificationId != null &&
      !options.some((o) => o.id === formData.value.classificationId)
    ) {
      formData.value.classificationId = null
    }
  } catch (error) {
    if (token !== classificationRequestToken) return
    console.error('Error cargando clasificaciones:', error)
    classificationOptions.value = []
    classificationsError.value = 'No se pudo cargar el catálogo de clasificaciones'
  } finally {
    if (token === classificationRequestToken) loadingClassifications.value = false
  }
}

const validationErrors = ref<IncidentFormValidationErrors>({})

// Computed
const localShow = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const isEditing = computed(() => !!props.incident)

const agencyOptions = computed(() => {
  return props.agencies.map(agency => ({
    id: agency.id,
    name: agency.name,
    address: agency.address,
    status: agency.status,
    value: agency.id,
    title: agency.name
  }))
})

// Opciones para selects
const priorityOptions = [
  { title: 'Crítica', value: 'CRITICAL' },
  { title: 'Alta', value: 'HIGH' },
  { title: 'Media', value: 'MEDIUM' },
  { title: 'Baja', value: 'LOW' }
]

const categoryOptions = [
  { title: 'Personal', value: 'PERSONAL' },
  { title: 'Técnica', value: 'TECHNICAL' },
  { title: 'Infraestructura', value: 'INFRASTRUCTURE' },
  { title: 'Eléctrica', value: 'ELECTRICAL' },
  { title: 'Internet', value: 'INTERNET' },
  { title: 'Equipos', value: 'EQUIPMENT' },
  { title: 'Otra', value: 'OTHER' }
]

const statusOptions = [
  { title: 'Pendiente', value: 'PENDING' },
  { title: 'En Proceso', value: 'IN_PROGRESS' },
  { title: 'Pendiente Validación', value: 'PENDING_VALIDATION' },
  { title: 'Cerrado', value: 'CLOSED' },
  { title: 'Cancelado', value: 'CANCELLED' }
]

// Reglas de validación
const titleRules = [
  (v: string) => !!v || 'El título es requerido',
  (v: string) => (v && v.length >= 10) || 'El título debe tener al menos 10 caracteres',
  (v: string) => (v && v.length <= 100) || 'El título no puede exceder 100 caracteres'
]

const descriptionRules = [
  (v: string) => !!v || 'La descripción es requerida',
  (v: string) => (v && v.length >= 20) || 'La descripción debe tener al menos 20 caracteres',
  (v: string) => (v && v.length <= 500) || 'La descripción no puede exceder 500 caracteres'
]

const priorityRules = [
  (v: string) => !!v || 'La prioridad es requerida'
]

const categoryRules = [
  (v: string) => !!v || 'La categoría es requerida'
]

const agencyRules = [
  (v: any) => !!v || 'La agencia es requerida'
]

// Métodos
const initializeForm = () => {
  if (props.incident) {
    // Modo edición - cargar datos existentes
    console.log('🔍 DEBUG initializeForm - incident completo:', props.incident)
    console.log('🔍 DEBUG initializeForm - incident.agencyId:', props.incident.agencyId)
    console.log('🔍 DEBUG initializeForm - incident.location:', props.incident.location)
    
    formData.value = {
      title: props.incident.title,
      description: props.incident.description,
      priority: props.incident.priority,
      category: props.incident.category,
      agencyId: props.incident.agencyId,
      classificationId: props.incident.classificationId ?? null,
      attachments: []
    }

    console.log('✅ DEBUG formData cargado:', formData.value)
  } else {
    // Modo creación — limpiar formulario. R2 (F5): NO se carga ningún catálogo aquí — el catálogo
    // es contexto del punto y se carga sólo cuando el usuario selecciona la agencia (watch).
    formData.value = {
      title: '',
      description: '',
      priority: 'MEDIUM',
      category: 'OTHER',
      agencyId: null,
      classificationId: null,
      attachments: []
    }
    classificationOptions.value = []
    classificationsError.value = null
  }
  
  validationErrors.value = {}
  
  nextTick(() => {
    formRef.value?.resetValidation()
  })
}

const handleSave = async () => {
  if (!formRef.value) return

  const { valid } = await formRef.value.validate()
  if (!valid) return

  // R2 (F5) — cinturón fail-closed: sólo puede enviarse una clasificación PRESENTE en el catálogo
  // vigente del punto seleccionado. Una selección stale (carrera de carga, catálogo vaciado por un
  // cambio de agencia) jamás viaja al intake.
  if (
    !isEditing.value &&
    (formData.value.classificationId == null ||
      !classificationOptions.value.some((o) => o.id === formData.value.classificationId))
  ) {
    validationErrors.value = {
      ...validationErrors.value,
      classificationError: 'Seleccione una clasificación del catálogo vigente de la agencia',
    }
    return
  }

  saving.value = true
  
  try {
    // P13-K2-VERTICAL@V1 M3 (F1-A) — CREAR y ACTUALIZAR construyen cuerpos DISTINTOS.
    //
    // El backend ratificó `INCIDENCE_CREATE_STATUS = SERVER_DERIVED_PENDING` (PR #150, head
    // c8603962…): el estado de una incidencia NUEVA lo deriva el servidor y el cuerpo de creación
    // NO es canal de ciclo de vida. Enviar `status` al crear — incluso el valor correcto `pending` —
    // se deniega con 403 INTAKE_BODY_STATUS_AS_AUTHORITY. Antes se construía UN solo objeto que
    // siempre incluía `status`, así que toda creación desde esta consola quedaba rota contra el
    // contrato vigente.
    //
    // Los dos cuerpos se construyen como literales SEPARADOS y anotados (nunca por spread de un
    // objeto común): así el chequeo de propiedades en exceso de TypeScript rechaza un `status` que
    // vuelva a colarse en creación — con un spread ese chequeo no se aplica y el campo podría
    // reaparecer en silencio. `CreateIncidentRequest` (src/types/incidents.ts) no declara `status`.
    //
    // P13-V1-V6B: la ACTUALIZACIÓN tampoco es canal de ciclo de vida. El estado se cambia sólo por
    // sus transiciones nombradas; el cuerpo de edición lleva metadata no autoritativa y nada más.
    if (isEditing.value) {
      // P13-V1-V6B REPAIR-03 — the generic update carries NON-AUTHORITATIVE METADATA ONLY. The
      // backend answers a stable 400 UPDATE_FIELD_NOT_ALLOWED to anything else, so sending more
      // would not "almost work": it would fail the whole edit. `status` moved to its named
      // transitions; `category` and `agencyId` are creation-only and are not rendered while editing.
      const updateData: UpdateIncidentRequest = {
        title: formData.value.title,
        description: formData.value.description,
        priority: formData.value.priority?.toLowerCase() as any
      }

      emit('save', updateData)
      return
    }

    const createData: CreateIncidentRequest = {
      title: formData.value.title,
      description: formData.value.description,
      priority: formData.value.priority?.toLowerCase() as any,
      category: formData.value.category?.toLowerCase() as any,
      // `agencyRules` ya exige la agencia antes de llegar aquí; la aserción conserva EXACTAMENTE el
      // comportamiento previo (`|| undefined`) sin ampliar el tipo de creación.
      agencyId: (formData.value.agencyId || undefined) as CreateIncidentRequest['agencyId'],
      // P13-V1C — clasificación OBLIGATORIA (classificationRules la exige antes de llegar aquí).
      // ÚNICO canal del routing: el servidor deriva política y departamento; jamás viajan aliases.
      classificationId: formData.value.classificationId as number,
    }

    emit('save', createData)
  } catch (error) {
    console.error('Error en el formulario:', error)
    showNotification('Error al procesar el formulario', 'error')
  } finally {
    saving.value = false
  }
}

const handleCancel = () => {
  localShow.value = false
  emit('cancel')
}

const handleAgencySearch = (search: string) => {
  agencySearch.value = search
  // Aquí se podría implementar búsqueda en el servidor si es necesario
}

// Métodos auxiliares para iconos y colores
const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success'
  }
  return colors[priority] || 'grey'
}

const getPriorityIcon = (priority: string) => {
  const icons: Record<string, string> = {
    CRITICAL: 'mdi-alert-circle',
    HIGH: 'mdi-alert',
    MEDIUM: 'mdi-information',
    LOW: 'mdi-check-circle'
  }
  return icons[priority] || 'mdi-help'
}

const getCategoryColor = (category: string) => {
  const colors: Record<string, string> = {
    PERSONAL: 'purple',
    TECHNICAL: 'blue',
    INFRASTRUCTURE: 'orange',
    ELECTRICAL: 'yellow',
    INTERNET: 'green',
    EQUIPMENT: 'teal',
    OTHER: 'grey'
  }
  return colors[category] || 'grey'
}

const getCategoryIcon = (category: string) => {
  const icons: Record<string, string> = {
    PERSONAL: 'mdi-account-group',
    TECHNICAL: 'mdi-laptop',
    INFRASTRUCTURE: 'mdi-office-building',
    ELECTRICAL: 'mdi-flash',
    INTERNET: 'mdi-wifi',
    EQUIPMENT: 'mdi-wrench',
    OTHER: 'mdi-help-circle'
  }
  return icons[category] || 'mdi-help'
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    PENDING_VALIDATION: 'purple',
    CLOSED: 'grey',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status: string) => {
  const icons: Record<string, string> = {
    PENDING: 'mdi-clock-outline',
    IN_PROGRESS: 'mdi-progress-clock',
    PENDING_VALIDATION: 'mdi-clipboard-check-outline',
    CLOSED: 'mdi-check-all',
    CANCELLED: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help'
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    initializeForm()
  }
})

// R2 (F5) — el catálogo sigue a la AGENCIA: cambiarla limpia la selección (una clasificación del
// catálogo anterior no es válida para el nuevo punto) y recarga con el nuevo pointId; sin agencia,
// catálogo vacío y selección nula. Sólo en creación (en edición no se renderiza el campo).
watch(() => formData.value.agencyId, (agencyId) => {
  if (isEditing.value) return
  formData.value.classificationId = null
  if (validationErrors.value.classificationError) {
    validationErrors.value = { ...validationErrors.value, classificationError: undefined }
  }
  loadClassifications(agencyId ? Number(agencyId) : null)
})

watch(() => props.incident, () => {
  if (props.modelValue) {
    initializeForm()
  }
})
</script>

<style scoped>
.incident-form-dialog {
  background: white;
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 1.5rem 2rem;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.dialog-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

/* Mejoras en los campos del formulario */
:deep(.v-field--variant-outlined) {
  border-radius: 8px;
}

:deep(.v-expansion-panel) {
  border-radius: 8px !important;
  overflow: hidden;
}

:deep(.v-expansion-panel-title) {
  padding: 1rem 1.5rem;
  background: #f8f9fa;
}

:deep(.v-expansion-panel-text) {
  padding: 1.5rem;
}

/* Estilos para chips de archivos */
:deep(.v-file-input .v-field__input) {
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .dialog-header {
    padding: 1rem;
  }
  
  .dialog-title {
    font-size: 1.25rem;
  }
  
  :deep(.v-card-text) {
    padding: 1rem !important;
  }
  
  .dialog-actions {
    padding: 1rem !important;
    padding-top: 0 !important;
  }
}

/* Estados de validación */
:deep(.v-text-field--error .v-field__outline) {
  border-color: #dc3545 !important;
  border-width: 2px !important;
}

:deep(.v-text-field--error .v-field__append-inner) {
  color: #dc3545 !important;
}

/* Animaciones */
.incident-form-dialog {
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Mejoras de accesibilidad */
:deep(.v-btn:focus-visible) {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

:deep(.v-field:focus-within) {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
}
</style>
